import React, {forwardRef, useState, useEffect} from "react";
import Modal from '../Modal';
import { getFlowNodeStyles, getTextContainerStyles } from './FlowNode_style.js';

const FlowNode = forwardRef(({ children, style, className = '', modalContent }, ref) => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [proximityGlow, setProximityGlow] = useState(0);
    
    // Check if children is just plain text/string
    const isPlainText = typeof children === 'string' || typeof children === 'number';

    const handleClick = () => {
        if (modalContent) {
            setIsModalOpen(true);
        }
    };

    // Calculate proximity-based glow effect
    useEffect(() => {
        if (!modalContent || !ref.current) return; // Only for clickable nodes
        
        const handleMouseMove = (e) => {
            const nodeRect = ref.current.getBoundingClientRect();
            
            // Calculate distance to nearest edge of the box
            const mouseX = e.clientX;
            const mouseY = e.clientY;
            
            // If mouse is inside the box, distance is 0
            if (mouseX >= nodeRect.left && mouseX <= nodeRect.right &&
                mouseY >= nodeRect.top && mouseY <= nodeRect.bottom) {
                setProximityGlow(1);
                return;
            }
            
            // Calculate distance to nearest edge
            const dx = Math.max(0, Math.max(nodeRect.left - mouseX, mouseX - nodeRect.right));
            const dy = Math.max(0, Math.max(nodeRect.top - mouseY, mouseY - nodeRect.bottom));
            const distance = Math.sqrt(dx * dx + dy * dy);
            
            // Maximum distance for effect (100px)
            const maxDistance = 100;
            const proximity = Math.max(0, 1 - (distance / maxDistance));
            setProximityGlow(proximity);
        };

        document.addEventListener('mousemove', handleMouseMove);
        return () => document.removeEventListener('mousemove', handleMouseMove);
    }, [modalContent, ref]);

    const nodeStyles = getFlowNodeStyles(className, isPlainText);
    const textStyles = getTextContainerStyles();

    // Create proximity-enhanced glow effect
    const enhancedNodeStyles = modalContent ? {
        ...nodeStyles,
        boxShadow: `
            0 0 ${20 + (proximityGlow * 40)}px rgba(108, 163, 208, ${0.35 + (proximityGlow * 0.25)}),
            0 0 ${13 + (proximityGlow * 27)}px rgba(173, 218, 239, ${proximityGlow * 0.8}),
            inset 0 0 ${7 + (proximityGlow * 13)}px rgba(255, 255, 255, ${proximityGlow * 0.3})
        `,
        border: `${nodeStyles.border.split(' ')[0]} solid rgba(64, 230, 202, ${0.2 + (proximityGlow * 0.4)})`
    } : nodeStyles;

    return (
        <>
            <div 
                ref={ref} 
                style={{
                    ...enhancedNodeStyles,
                    ...style,
                    cursor: modalContent ? 'pointer' : 'default'
                }}
                onClick={handleClick}
            >
                {isPlainText ? (
                    <div style={textStyles}>
                        {children}
                    </div>
                ) : (
                    children
                )}
            </div>
            
            {modalContent && (
                <Modal 
                    isOpen={isModalOpen} 
                    onClose={() => setIsModalOpen(false)}
                    title={modalContent.title}
                >
                    {modalContent.body}
                </Modal>
            )}
        </>
    );
});

export default FlowNode;